import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  Bell,
  BellOff,
  Check,
  CheckCheck,
  Clock,
  FileText,
  Filter,
  MessageSquare,
  Megaphone,
  Settings,
  Trash2,
  User,
} from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  useApproveLeave, useRejectLeave, useApproveReimbursement, useRejectReimbursement,
  useDoNotDisturb,
  useUpdateEfficiencyPreferences,
} from '@/hooks'
import {
  useNotifications,
  useMarkAsRead,
  useMarkAllAsRead,
  useDeleteNotification,
  type Notification,
  type NotificationType,
  type NotificationFilters,
} from '@/hooks'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_authenticated/notifications/')({
  component: NotificationsPage,
})

// ==================== 常量 ====================

const NOTIFICATION_TYPE_ICONS: Record<NotificationType, typeof Bell> = {
  approval_request: FileText,
  approval_result: Check,
  task_assigned: User,
  task_mentioned: MessageSquare,
  system_announcement: Megaphone,
}

const NOTIFICATION_TYPE_LABELS: Record<NotificationType, string> = {
  approval_request: '审批请求',
  approval_result: '审批结果',
  task_assigned: '任务分配',
  task_mentioned: '任务提及',
  system_announcement: '系统公告',
}

const NOTIFICATION_TYPES: NotificationType[] = [
  'approval_request',
  'approval_result',
  'task_assigned',
  'task_mentioned',
  'system_announcement',
]

// ==================== 相对时间格式化 ====================

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)

  if (diffSeconds < 60) {
    return '刚刚'
  } else if (diffMinutes < 60) {
    return `${diffMinutes} 分钟前`
  } else if (diffHours < 24) {
    return `${diffHours} 小时前`
  } else if (diffDays < 7) {
    return `${diffDays} 天前`
  } else {
    return date.toLocaleDateString('zh-CN')
  }
}

// ==================== 通知项组件 ====================

interface NotificationAction {
  label: string
  variant?: 'default' | 'primary' | 'destructive'
  onClick: () => void
  isLoading?: boolean
}

interface NotificationItemProps {
  notification: Notification
  onMarkAsRead: (id: string) => void
  onDelete: (id: string) => void
  isMarkingRead: boolean
  isDeleting: boolean
  actions?: NotificationAction[]
}

function NotificationItem({
  notification,
  onMarkAsRead,
  onDelete,
  isMarkingRead,
  isDeleting,
  actions = [],
}: NotificationItemProps) {
  const Icon = NOTIFICATION_TYPE_ICONS[notification.type]

  return (
    <div
      className={cn(
        'flex gap-4 p-4 border-b last:border-b-0 transition-colors',
        !notification.isRead && 'bg-accent/30'
      )}
    >
      <div
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
          notification.isRead ? 'bg-muted' : 'bg-primary/10'
        )}
      >
        <Icon className={cn('h-5 w-5', notification.isRead ? 'text-muted-foreground' : 'text-primary')} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={cn('font-medium', !notification.isRead && 'text-foreground')}>
                {notification.title}
              </span>
              {!notification.isRead && <span className="h-2 w-2 rounded-full bg-primary shrink-0" />}
            </div>
            <p className="text-sm text-muted-foreground mt-1">{notification.content}</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatRelativeTime(notification.createdAt)}
              </span>
              <span className="px-1.5 py-0.5 rounded bg-muted">
                {NOTIFICATION_TYPE_LABELS[notification.type]}
              </span>
            </div>

            {/* 内联操作按钮 */}
            {actions.length > 0 && (
              <div className="flex items-center gap-2 mt-3">
                {actions.map((action, index) => (
                  <Button
                    key={index}
                    size="sm"
                    variant={action.variant === 'destructive' ? 'destructive' : action.variant === 'primary' ? 'default' : 'outline'}
                    onClick={action.onClick}
                    disabled={action.isLoading}
                    className="h-7 text-xs"
                  >
                    {action.isLoading ? (
                      <span className="h-3 w-3 animate-spin rounded-full border border-current border-t-transparent mr-1" />
                    ) : null}
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {!notification.isRead && (
              <button
                onClick={() => onMarkAsRead(notification.id)}
                disabled={isMarkingRead}
                className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-foreground disabled:opacity-50"
                title="标记已读"
              >
                <Check className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={() => onDelete(notification.id)}
              disabled={isDeleting}
              className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive disabled:opacity-50"
              title="删除"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ==================== 主页面组件 ====================

function NotificationsPage() {
  const navigate = useNavigate()
  const [filters, setFilters] = useState<NotificationFilters>({
    page: 1,
    pageSize: 50,
  })

  const { data, isLoading } = useNotifications(filters)
  const markAsRead = useMarkAsRead()
  const markAllAsRead = useMarkAllAsRead()
  const deleteNotification = useDeleteNotification()

  // Do not disturb mode
  const doNotDisturb = useDoNotDisturb()
  const updatePreferences = useUpdateEfficiencyPreferences()

  // Approval hooks
  const approveLeave = useApproveLeave()
  const rejectLeave = useRejectLeave()
  const approveReimbursement = useApproveReimbursement()
  const rejectReimbursement = useRejectReimbursement()

  const notifications = data?.data ?? []
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 0
  const currentPage = data?.page ?? 1

  const hasUnread = notifications.some((n) => !n.isRead)

  const handleToggleDoNotDisturb = () => {
    updatePreferences.mutate({ doNotDisturb: !doNotDisturb })
  }

  // Generate actions for a notification based on its type and related data
  const getNotificationActions = (notification: Notification): NotificationAction[] => {
    const actions: NotificationAction[] = []

    // Approval request notifications - show approve/reject buttons
    if (notification.type === 'approval_request' && notification.relatedId) {
      if (notification.relatedType === 'leave') {
        actions.push({
          label: '通过',
          variant: 'primary',
          onClick: () => {
            if (notification.relatedId) {
              approveLeave.mutate(notification.relatedId, {
                onSuccess: () => {
                  markAsRead.mutate(notification.id)
                },
              })
            }
          },
          isLoading: approveLeave.isPending,
        })
        actions.push({
          label: '拒绝',
          variant: 'destructive',
          onClick: () => {
            if (notification.relatedId) {
              rejectLeave.mutate({ id: notification.relatedId, reason: '不符合审批条件' }, {
                onSuccess: () => {
                  markAsRead.mutate(notification.id)
                },
              })
            }
          },
          isLoading: rejectLeave.isPending,
        })
      } else if (notification.relatedType === 'reimbursement') {
        actions.push({
          label: '通过',
          variant: 'primary',
          onClick: () => {
            if (notification.relatedId) {
              approveReimbursement.mutate(notification.relatedId, {
                onSuccess: () => {
                  markAsRead.mutate(notification.id)
                },
              })
            }
          },
          isLoading: approveReimbursement.isPending,
        })
        actions.push({
          label: '拒绝',
          variant: 'destructive',
          onClick: () => {
            if (notification.relatedId) {
              rejectReimbursement.mutate({ id: notification.relatedId, reason: '不符合审批条件' }, {
                onSuccess: () => {
                  markAsRead.mutate(notification.id)
                },
              })
            }
          },
          isLoading: rejectReimbursement.isPending,
        })
      }
    }

    // Task assigned notifications - show view task button
    if (notification.type === 'task_assigned' && notification.relatedId) {
      actions.push({
        label: '查看任务',
        variant: 'default',
        onClick: () => {
          if (notification.relatedId) {
            void navigate({ to: '/pm/issues/$issueId', params: { issueId: notification.relatedId } })
          }
        },
      })
    }

    // Task mentioned notifications - show view comment button
    if (notification.type === 'task_mentioned' && notification.relatedId) {
      actions.push({
        label: '查看任务',
        variant: 'default',
        onClick: () => {
          if (notification.relatedId) {
            void navigate({ to: '/pm/issues/$issueId', params: { issueId: notification.relatedId } })
          }
        },
      })
    }

    return actions
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">通知中心</h1>
          <p className="text-muted-foreground">管理您的所有通知</p>
        </div>
        <a
          href="/settings/my-notifications"
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border bg-background hover:bg-muted transition-colors"
        >
          <Settings className="h-4 w-4" />
          通知设置
        </a>
      </div>

      {/* 勿扰模式提示 */}
      {doNotDisturb && (
        <div className="flex items-center justify-between gap-4 p-4 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800">
          <div className="flex items-center gap-3">
            <BellOff className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            <div>
              <p className="font-medium text-amber-800 dark:text-amber-300">勿扰模式已开启</p>
              <p className="text-sm text-amber-700 dark:text-amber-400">仅显示紧急通知</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleToggleDoNotDisturb}
            disabled={updatePreferences.isPending}
          >
            关闭勿扰
          </Button>
        </div>
      )}

      {/* 筛选和操作栏 */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-4 rounded-lg border bg-card">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">筛选：</span>
          </div>

          {/* 类型筛选 */}
          <Select
            value={filters.type ?? 'all'}
            onValueChange={(value) =>
              setFilters((prev) => ({
                ...prev,
                type: value === 'all' ? undefined : (value as NotificationType),
                page: 1,
              }))
            }
          >
            <SelectTrigger className="w-32">
              <SelectValue placeholder="全部类型" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部类型</SelectItem>
              {NOTIFICATION_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {NOTIFICATION_TYPE_LABELS[type]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* 已读状态筛选 */}
          <Select
            value={filters.isRead === undefined ? 'all' : filters.isRead ? 'read' : 'unread'}
            onValueChange={(value) =>
              setFilters((prev) => ({
                ...prev,
                isRead: value === 'all' ? undefined : value === 'read',
                page: 1,
              }))
            }
          >
            <SelectTrigger className="w-28">
              <SelectValue placeholder="全部状态" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部状态</SelectItem>
              <SelectItem value="unread">未读</SelectItem>
              <SelectItem value="read">已读</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-4">
          {/* 勿扰模式开关 */}
          <div className="flex items-center gap-2">
            <BellOff className={cn('h-4 w-4', doNotDisturb ? 'text-primary' : 'text-muted-foreground')} />
            <span className="text-sm text-muted-foreground">勿扰</span>
            <Switch
              checked={doNotDisturb}
              onCheckedChange={handleToggleDoNotDisturb}
              disabled={updatePreferences.isPending}
            />
          </div>

          {/* 批量操作 */}
          {hasUnread && (
            <button
              onClick={() => markAllAsRead.mutate()}
              disabled={markAllAsRead.isPending}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
            >
              <CheckCheck className="h-4 w-4" />
              全部标记已读
            </button>
          )}
        </div>
      </div>

      {/* 通知列表 */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="rounded-lg border bg-card">
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Bell className="h-12 w-12 mb-4" />
            <span className="text-lg font-medium">暂无通知</span>
            <span className="text-sm mt-1">
              {filters.type || filters.isRead !== undefined ? '没有符合筛选条件的通知' : '您还没有收到任何通知'}
            </span>
          </div>
        </div>
      ) : (
        <div className="rounded-lg border bg-card">
          {notifications.map((notification) => (
            <NotificationItem
              key={notification.id}
              notification={notification}
              onMarkAsRead={(id) => markAsRead.mutate(id)}
              onDelete={(id) => deleteNotification.mutate(id)}
              isMarkingRead={markAsRead.isPending}
              isDeleting={deleteNotification.isPending}
              actions={getNotificationActions(notification)}
            />
          ))}
        </div>
      )}

      {/* 分页 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">
            共 {total} 条通知，第 {currentPage} / {totalPages} 页
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page ?? 1) - 1 }))}
              disabled={currentPage <= 1}
              className="px-3 py-1.5 text-sm font-medium rounded-md border bg-background hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              上一页
            </button>
            <button
              onClick={() => setFilters((prev) => ({ ...prev, page: (prev.page ?? 1) + 1 }))}
              disabled={currentPage >= totalPages}
              className="px-3 py-1.5 text-sm font-medium rounded-md border bg-background hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              下一页
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
